import { SubmitPlayerGameVotes, VoteType, GetGameViewById } from "@grassrootly/api";
import useBaseRequest from "../../api/BaseRequest";
import { useUser } from "../../api/auth/AuthSelector";
import { useGameView } from "../../api/game/GameViewSelector";
import { useAppDispatch } from "@grassrootly/rn-core";
import { gameViewActions } from "../../api/game/GameViewReducer";
import { adaptGameViewToGame } from "../game/gameViewAdapter";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigation/Navigation.types";
import { useCallback, useEffect } from "react";
import { useAlert } from "@grassrootly/rn-core";

export function useGameVote(gameId: number, navigation: NativeStackNavigationProp<RootStackParamList, "vote">) {
  const user = useUser();
  const dispatch = useAppDispatch();
  const gameView = useGameView(gameId);
  const game = gameView ? adaptGameViewToGame(gameView) : null;
  const { danger } = useAlert();

  // Function to refresh game data after voting
  const { execute: refreshGameView, loading: fetching } = useBaseRequest(GetGameViewById, {
    onCompleted: (data) => {
      if (!data) return;
      dispatch(gameViewActions.setGameView(data));
    },
  });

  const fetchGame = () => {
    refreshGameView({ id: gameId });
  };

  // Fetch game data on mount if not already loaded
  useEffect(() => {
    if (!gameView && !fetching) {
      fetchGame();
    }
  }, [gameId, gameView, fetching]);

  const { execute, loading: submitting } = useBaseRequest(SubmitPlayerGameVotes, {
    onCompleted: (data) => {
      fetchGame(); // Refresh game data to get updated vote information
      navigation.goBack();
    },
    onError: (error) => {
      console.error('Vote submission error:', error);
      // Always refresh game data even on error to ensure UI is in sync
      fetchGame();
      // Don't show error to user - votes are handled gracefully on server side
      // Navigate back so user doesn't get stuck on vote screen
      navigation.goBack();
    },
  });

  const submitVotes = (votes: Array<{ type: VoteType; attendanceId: number }>) => {
    // Prevent double submission if already submitting
    if (submitting) {
      return;
    }
    
    if (!hasAttended()) {
      danger({ message: "Only players that have played the game can vote.", title: "Not allowed" });
      return;
    }
    execute({
      votes: votes.map((v) => ({ ...v, id: user?.id! })),
    });
  };

  const hasAttended = useCallback(() => {
    return !!game?.attendees?.find((a) => a.player_id === user?.id && a.attended);
  }, [game, user]);

  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.replace("main");
  };

  return { game, saving: fetching || submitting, submitVotes, goBack };
}
